import {capitalize, getOwnAttr} from 'common/util';
import {MPProperties, NON_NUMERIC_VALUES_SEGMENT, REMAINING_VALUES_SEGMENT} from 'common/constants';
import type {TooltipAttrs} from 'common/components/tooltip/types';

import {getPropertyDisplayName, getPropertyDisplayNameOld} from '../display-name-registry';
import {MappedPropertyValues, ResourceType} from '../constants';
import {renameEvent} from './events';
import {getPropertyValueMappings} from './mappings';

type MetaOptions = {isCustom?: boolean};

export const propertiesUtils = {
  renameProperty,
};

export enum ConstantPropertyValues {
  EmptyString = ``,
  EmptyList = `$empty_list`,
  NotSet = `undefined`,
  NotApplicable = `$not_applicable`,
  NotANumber = `$non_numeric_values`,
}
export const ConstantPropertyValueSet = new Set<ConstantPropertyValues>(Object.values(ConstantPropertyValues));

export type ConstantPropertyValueInfo = {label: string; tooltip?: TooltipAttrs};
export const ConstantPropertyValuesInfo: Record<ConstantPropertyValues, ConstantPropertyValueInfo> = {
  [ConstantPropertyValues.NotANumber]: {
    label: `(not a number)`,
    tooltip: {
      text: `This property contains an invalid number representation`,
    },
  },
  [ConstantPropertyValues.EmptyList]: {
    label: `(empty list)`,
    tooltip: {
      text: `This property is set to an empty list.\n[Learn More](https://help.mixpanel.com/hc/en-us/articles/360047094171)`,
    },
  },
  [ConstantPropertyValues.NotSet]: {
    label: `(not set)`,
    tooltip: {
      text: `This property does not contain any value.\n[Learn More](https://help.mixpanel.com/hc/en-us/articles/115004499403-Undefined-Null-Properties)`,
    },
  },
  [ConstantPropertyValues.EmptyString]: {
    label: `(empty string)`,
    tooltip: {text: `This property is set to an empty string value`},
  },
  [ConstantPropertyValues.NotApplicable]: {
    label: `(not applicable)`,
  },
};

export function getIconForPropertyType(type, {isCustom}: MetaOptions = {isCustom: false}): string {
  const iconNameBuffer = [`type`, isCustom && `custom`].filter(Boolean);

  switch (type) {
    case `dimension`:
      return `table`;
    case `string`:
      iconNameBuffer.push(`text`);
      break;
    case `datetime`:
      iconNameBuffer.push(`date`);
      break;
    case `boolean`:
    case `list`:
    case `number`:
    case `object`:
      iconNameBuffer.push(type);
      break;
    default:
      return null;
  }
  return iconNameBuffer.join(`-`);
}

const PROPERTY_TYPE_LABEL_MAP = {
  boolean: `True / False`,
  datetime: `Date`,
  list: `List`,
  number: `Number`,
  object: `Object`,
  string: `String`,
};

export function getLabelForPropertyType(type, {isCustom}: MetaOptions = {isCustom: false}) {
  const label = PROPERTY_TYPE_LABEL_MAP[type];
  return label && isCustom ? `Custom ${label}` : label;
}

/*
 * dataGroupId and customProperty are not optional parameters. If the property in question
 * could be a B2B property or custom property and those two parameters are not passed in the
 * property may be improperly renamed.
 */
function renameProperty(
  propertyName: string | string[],
  resourceType: ResourceType,
  {dataGroupId = null, customProperty = null, propertyObjectKey = null} = {},
): string {
  if (customProperty && customProperty.name) {
    if (Array.isArray(propertyName)) {
      return [customProperty.name, renameProperty(propertyName[1], resourceType, {dataGroupId})].join(`.`);
    } else {
      return customProperty.name;
    }
  } else if (Array.isArray(propertyName)) {
    return [
      renameProperty(propertyName[0], resourceType),
      renameProperty(propertyName[1], resourceType, {dataGroupId, customProperty}),
    ].join(`.`);
  } else {
    // rename object properties
    if (Array.isArray(propertyObjectKey)) {
      return [propertyName, ...propertyObjectKey]
        .map((property, index) => {
          /** handles displaying experiment names */
          if (index > 0 && propertyName === MPProperties.Experiments) {
            return renamePropertyValue(property, MappedPropertyValues.ExperimentId);
          }

          return renameProperty(property, resourceType, {dataGroupId, customProperty});
        })
        .join(`.`);
    }

    const renamedProperty = getPropertyDisplayName(propertyName, resourceType, dataGroupId);
    if (renamedProperty !== propertyName) {
      return renamedProperty;
    }

    // default conversion for all properties starting with '$'
    if (propertyName && propertyName.length > 1 && propertyName.startsWith(`$`)) {
      return propertyName
        .slice(1)
        .split(`_`)
        .map((str) => capitalize(str))
        .join(` `);
    }
    return propertyName;
  }
}

/*
 * dataGroupId and customProperty are not optional parameters. If the property in question
 * could be a B2B property or custom property and those two parameters are not passed in the
 * property may be improperly renamed.
 */
// TODO: renameProperty and getPropertyDisplayName are flawed. It should be checking against
// the right property definition store based on resourceType ('event' or 'user'), which they
// currently cannot do only taking the propertyName as argument
/**
 * @deprecated - use renameProperty instead
 */
export function renamePropertyOld(
  propertyName: string | string[],
  {dataGroupId = null, customProperty = null} = {},
): string {
  if (customProperty && customProperty.name) {
    if (Array.isArray(propertyName)) {
      return [customProperty.name, renamePropertyOld(propertyName[1], {dataGroupId})].join(`.`);
    } else {
      return customProperty.name;
    }
  } else if (Array.isArray(propertyName)) {
    return [renamePropertyOld(propertyName[0]), renamePropertyOld(propertyName[1], {dataGroupId, customProperty})].join(
      `.`,
    );
  } else {
    const renamedProperty = getPropertyDisplayNameOld(propertyName);
    if (renamedProperty !== propertyName) {
      return renamedProperty;
    }

    // default conversion for all properties starting with '$'
    if (propertyName && propertyName.length > 1 && propertyName.startsWith(`$`)) {
      return propertyName
        .slice(1)
        .split(`_`)
        .map((str) => capitalize(str))
        .join(` `);
    }
    return propertyName;
  }
}

export function renamePropertyValue(value, propertyName): string {
  if (value === REMAINING_VALUES_SEGMENT) {
    return `(remaining values)`;
  }
  if (value === NON_NUMERIC_VALUES_SEGMENT) {
    return `(non-numeric values)`;
  }
  if (propertyName === `$event_name` || propertyName === `$event`) {
    // Value is an event
    return renameEvent(value);
  } else if (propertyName === `$people`) {
    // Value is a property
    return renamePropertyOld(value); // value is $all_people
  }

  // check against special property values (empty list, undefined, emtpy string, etc...)
  const constantPropertyValue = getOwnAttr(ConstantPropertyValuesInfo, value);
  if (constantPropertyValue) {
    return constantPropertyValue.label;
  }

  const id = parseInt(value, 10);
  const propertyValueMappings = getPropertyValueMappings();
  if (!propertyValueMappings || Number.isNaN(id)) {
    return value;
  }

  let mappedPropertyValue = value;
  const {campaignVariants = [], experiments = [], datasets = {}} = propertyValueMappings;
  switch (propertyName) {
    case MappedPropertyValues.Campaigns:
    case MappedPropertyValues.CampaignId: {
      // map campaign IDs to their names
      const campaign = campaignVariants.find((c) => c.id === id);
      if (campaign) {
        mappedPropertyValue = campaign.name;
      }
      break;
    }
    case MappedPropertyValues.MessageId: {
      // map message variant IDs to their numbers and campaign
      const campaign = campaignVariants.find((c) => c.variants.includes(id));
      if (campaign) {
        const variantIdx = campaign.variants.indexOf(id);
        mappedPropertyValue =
          variantIdx === 0 ? `${campaign.name} - Original variant` : `${campaign.name} - Variant ${variantIdx}`;
      }
      break;
    }
    case MappedPropertyValues.ExperimentId: {
      // map experiment IDs to their names
      const experiment = experiments.find((exp) => exp.id === id);
      if (experiment) {
        mappedPropertyValue = experiment.name;
      }
      break;
    }
    case MappedPropertyValues.VariantId: {
      // map experiment variant IDs to their numbers and experiment
      const experiment = experiments.find((exp) => exp.variants.some((variant) => variant.id === id));
      if (experiment) {
        const variantIdx = experiment.variants
          .map((variant) => variant.id)
          .sort((a, b) => a - b)
          .indexOf(id);
        mappedPropertyValue =
          variantIdx === 0 ? `${experiment.name} - Original variant` : `${experiment.name} - Variant ${variantIdx}`;
      }
      break;
    }
    case MappedPropertyValues.Dataset: {
      const dataset = datasets[value];
      if (dataset) {
        mappedPropertyValue = `${dataset.name} (id: ${dataset.projectId})`;
      }
      break;
    }
    default:
      break;
  }

  return mappedPropertyValue;
}
